import React from "react";
import { ChevronDown } from "lucide-react";
import GlassCard from "./GlassCard";

// Helper for the pill tags - NOW SHOWS ALL (No Limit)
function BulletRow({ label, items, onChipClick }) {
  // Safe check to ensure we always have an array
  const safeItems = items || [];

  return (
    <div style={{ marginTop: 12 }}>
      <div
        className="muted"
        style={{
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: "1px",
          marginBottom: 6,
          fontWeight: 700,
          opacity: 0.8,
        }}
      >
        {label}
      </div>

      {/* Removed the slice/limit logic. 
         flexWrap: "wrap" ensures they flow naturally to the next line 
      */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {safeItems.map((t, i) => (
          <button
            key={i}
            // Stop propagation prevents the card from closing when you click a chip
            onClick={(e) => {
              e.stopPropagation();
              if (onChipClick) onChipClick(t);
            }}
            className="chip"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              padding: "4px 10px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 500,
              color: "var(--ink)",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(34, 197, 94, 0.15)";
              e.currentTarget.style.borderColor = "var(--brand)";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.04)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
              e.currentTarget.style.color = "var(--ink)";
            }}
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function RakimCard({ item, open, onToggle, idx, onChipClick }) {
  const regionId = `rk-card-${idx}`;

  return (
    <GlassCard
      onClick={onToggle}
      className="rk-card"
      highlight={open}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      aria-controls={regionId}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      style={{
        cursor: "pointer",
        transition: "all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        transform: open ? "scale(1.01)" : "scale(1)",
        background: open ? "rgba(15, 23, 42, 0.8)" : "rgba(15, 23, 42, 0.4)",
        position: "relative",
      }}
    >
      {/* Top Row: Tag */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 8,
        }}
      >
        <div
          className="muted"
          style={{
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: "1px",
            fontWeight: 800,
            color: open ? "var(--brand)" : "var(--muted)",
            transition: "color 0.2s",
          }}
        >
          {item.tag}
        </div>
      </div>

      {/* Large Custom Chevron */}
      <div
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          color: open ? "var(--brand)" : "var(--ink)",
          opacity: open ? 1 : 0.7,
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "all 0.2s ease",
        }}
      >
        <ChevronDown size={24} />
      </div>

      {/* Hook */}
      <div
        style={{
          fontWeight: 800,
          fontSize: 17,
          lineHeight: 1.3,
          marginBottom: 12,
          color: "#fff",
          paddingRight: 32,
        }}
      >
        {item.hook}
      </div>

      {/* House Step */}
      <div>
        <div
          className="muted"
          style={{
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: "1px",
            marginBottom: 2,
            opacity: 0.7,
          }}
        >
          House step
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
          {item.house}
        </div>
      </div>

      {/* Training Chips - NOW SHOWING ALL */}
      <BulletRow
        label="In training"
        items={item.training}
        onChipClick={onChipClick}
      />

      {/* Expanded Content */}
      {open && (
        <div
          id={regionId}
          style={{ marginTop: 16, animation: "fadeIn 0.3s ease-out" }}
        >
          <div
            style={{ height: 1, background: "var(--line)", marginBottom: 16 }}
          />

          <BulletRow
            label="In mindset"
            items={item.mindset}
            onChipClick={onChipClick}
          />

          <div
            style={{
              marginTop: 16,
              fontSize: 13,
              color: "var(--brand)",
              fontWeight: 600,
              fontStyle: "italic",
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <span
              style={{
                height: 1,
                width: 20,
                background: "var(--brand)",
                opacity: 0.5,
              }}
            ></span>
            {item.punch}
          </div>

          <div
            style={{
              marginTop: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span className="muted" style={{ fontSize: 11 }}>
              Tap to close
            </span>
            <span
              style={{
                fontSize: 11,
                color: "var(--brand)",
                fontWeight: 700,
                cursor: "pointer",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Assessment →
            </span>
          </div>
        </div>
      )}

      {/* Collapsed Footer */}
      {!open && (
        <div
          className="muted"
          style={{
            marginTop: 16,
            paddingTop: 12,
            borderTop: "1px solid var(--line)",
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontStyle: "italic", opacity: 0.8 }}>
            {item.punch}
          </span>
        </div>
      )}
    </GlassCard>
  );
}
